import Config from 'react-native-config';
import { Dispatch } from 'redux';

import DeviceInfoServices from 'services/DeviceInfoServices';
import FileServices from 'services/FileServices';
import { apiKeySelector } from 'store/appSettings/selectors';
import { dfdAccessTokenSelector, fhirCurrentUserPatientId } from 'store/authentication/selectors';
import { consumerIdSelector } from 'store/profile/selectors';
import { RootState, Thunk } from 'store/types';
import {
  AdapterSource,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherResource,
  DispatcherEndpoint
} from 'modules/types/common';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { DFDHeaders } from 'serverTypes/authentication';
import { eobDownloadingSelector } from './selectors';

export const GET_EOB_DOCUMENTS = 'EOB_DOCUMENT.GET_DOCUMENTS';
export const GET_EOB_DOCUMENTS_SUCCESS = 'EOB_DOCUMENT.GET_DOCUMENTS_SUCCESS';
export const GET_EOB_DOCUMENTS_FAIL = 'EOB_DOCUMENT.GET_DOCUMENTS_FAIL';

export const getUrlAndHeaders = (
  isDateGreater: boolean,
  claimId: string | null,
  subscriberId: string | null = null
) => {
  if (isDateGreater) {
    return {
      url: getDispatcherUrl(
        DispatcherCategoryService.BILLING,
        DispatcherAuthPrefix.AUTHENTICATED,
        DispatcherResource.EOB,
        DispatcherEndpoint.NEW
      ),
      headers: {
        [DFDHeaders.mhpAdapter]: AdapterSource.UTAH,
        [DFDHeaders.resourceId]: claimId,
        [DFDHeaders.mhpSubscriberId]: subscriberId
      }
    };
  }
  return {
    url: getDispatcherUrl(
      DispatcherCategoryService.BILLING,
      DispatcherAuthPrefix.AUTHENTICATED,
      DispatcherResource.EOB,
      DispatcherEndpoint.DOWNLOAD
    ),
    headers: {
      [DFDHeaders.mhpAdapter]: AdapterSource.UTAH,
      [DFDHeaders.resourceId]: claimId
    }
  };
};

export const getEobDocuments = (
  claimId: string | null,
  subscriberId: string | null = null,
  isDateGreater = false
): Thunk => dispatch => {
  const url = isDateGreater
    ? `/new/?claimId=${claimId}&subscriberId=${subscriberId}`
    : `?claimId=${claimId}`;

  if (Config.BILLING_EXPANSION_ENABLED === 'enabled') {
    dispatch({
      type: GET_EOB_DOCUMENTS,
      payload: {
        client: 'dispatcher',
        request: {
          responseType: 'arraybuffer',
          method: 'get',
          url: getUrlAndHeaders(isDateGreater, claimId, subscriberId).url,
          headers: getUrlAndHeaders(isDateGreater, claimId, subscriberId).headers
        }
      }
    });
  } else {
    dispatch({
      type: GET_EOB_DOCUMENTS,
      payload: {
        client: 'eob',
        request: {
          responseType: 'arraybuffer',
          method: 'get',
          url
        }
      }
    });
  }
};

/**
 * Download EOB
 */
export const DOWNLOAD_EOB = 'EOB_DOCUMENT.DOWNLOAD_EOB';
export const DOWNLOAD_EOB_SUCCESS = 'EOB_DOCUMENT.DOWNLOAD_EOB_SUCCESS';
export const DOWNLOAD_EOB_FAIL = 'EOB_DOCUMENT.DOWNLOAD_EOB_FAIL';

export const downloadEob = (
  claimId: string | null,
  subscriberId: string | null = null,
  isDateGreater = false
) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();

  // Check for in flight downloads if there are any then return
  if (eobDownloadingSelector(state) === true) {
    return;
  }

  dispatch({ type: DOWNLOAD_EOB, payload: {} });

  const patientId = fhirCurrentUserPatientId(state);
  const accessToken = dfdAccessTokenSelector(state);
  const apikey = apiKeySelector(state);
  const consumerId = consumerIdSelector(state);
  const deviceId = DeviceInfoServices.getDeviceId();
  const timestamp = Date.now();

  try {
    if (Config.BILLING_EXPANSION_ENABLED === 'enabled') {
      const response = await FileServices.download(
        `eob-${timestamp}.pdf`,
        `${Config.DISPATCHER_BASE_URL}${
          getUrlAndHeaders(isDateGreater, claimId, subscriberId).url
        }`,
        {
          Authorization: `Bearer ${accessToken}`,
          apikey,
          client: 'dispatcher',
          responseType: 'arraybuffer',
          'x-device-id': deviceId,
          'x-dfd-consumer-id': consumerId,
          'x-patient-id': patientId,
          [DFDHeaders.mhpAdapter]: AdapterSource.UTAH,
          [DFDHeaders.resourceId]: claimId,
          [DFDHeaders.mhpSubscriberId]: isDateGreater ? subscriberId : null
        }
      );
      dispatch({ type: DOWNLOAD_EOB_SUCCESS, payload: { response } });
    } else {
      const url = isDateGreater
        ? `/new/?claimId=${claimId}&subscriberId=${subscriberId}`
        : `?claimId=${claimId}`;
      const response = await FileServices.download(
        `eob-${timestamp}.pdf`,
        `${Config.GRAPHQL_EOB_ENDPOINT_URL}${url}`,
        {
          Authorization: `Bearer ${accessToken}`,
          apikey,
          'x-device-id': deviceId,
          'x-dfd-consumer-id': consumerId,
          'x-patient-id': patientId
        }
      );
      dispatch({ type: DOWNLOAD_EOB_SUCCESS, payload: { response } });
    }
  } catch (err) {
    dispatch({ type: DOWNLOAD_EOB_FAIL, error: err });
  }
};
